<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6 text-wrap">
        {{
          $t(
            mode === "cancel"
              ? "acceptance.car_body.cancel_title"
              : "acceptance.car_body.release_title",
          )
        }}
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ error }}
        </v-alert>

        <template v-if="mode === 'cancel'">
          <div class="text-body-2 release-wrap mb-3">
            {{ $t("acceptance.car_body.cancel_body") }}
          </div>
          <v-textarea
            v-model="reason"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
            :label="$t('acceptance.car_body.reason')"
            :maxlength="reasonMax"
            counter
          />
        </template>

        <template v-else>
          <v-alert
            v-if="approvedDate"
            type="success"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{
              $t("acceptance.car_body.approved_hint", { date: approvedDate })
            }}
          </v-alert>
          <v-select
            v-model="form.containerId"
            :items="areaItems"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="comfortable"
            :label="$t('acceptance.car_body.area')"
            :no-data-text="$t('acceptance.car_body.no_area')"
          />
          <v-text-field
            v-model="form.weightKg"
            variant="outlined"
            density="comfortable"
            inputmode="decimal"
            suffix="kg"
            :label="$t('acceptance.car_body.weight')"
            :hint="$t('acceptance.car_body.weight_hint')"
            persistent-hint
            :error-messages="weightError ? [$t(weightError)] : []"
          />
          <div class="text-body-2 mt-3 mb-1">
            {{ $t("acceptance.car_body.parts_count", { count: partsCount }) }}
          </div>
          <v-checkbox
            v-model="form.confirmPartsRemoved"
            density="compact"
            hide-details
            :label="$t('acceptance.car_body.tick_parts')"
          />
          <v-checkbox
            v-model="form.confirmNoHazards"
            density="compact"
            hide-details
            class="release-wrap"
          >
            <template #label>
              <span class="release-wrap">
                {{ $t("acceptance.car_body.tick_hazards") }}
              </span>
            </template>
          </v-checkbox>
        </template>
      </v-card-text>
      <v-card-actions class="flex-wrap justify-end ga-2">
        <v-btn
          variant="text"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("workshop.form.cancel") }}
        </v-btn>
        <v-btn
          v-if="mode === 'cancel'"
          color="red-darken-3"
          variant="flat"
          :loading="saving"
          :disabled="saving || Boolean(reasonInvalid)"
          @click="$emit('confirm', { reason: reason.trim() })"
        >
          {{ $t("acceptance.car_body.cancel_confirm") }}
        </v-btn>
        <v-btn
          v-else
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="saving || !canSubmit"
          @click="$emit('confirm', body)"
        >
          {{ $t("acceptance.car_body.release_confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  REASON_MAX,
  buildRelease,
  canSubmitRelease,
  emptyReleaseForm,
  formatRomeDate,
  reasonError,
  releaseErrors,
} from "@/components/Waste/carBodyRelease.mjs";

// "Rilascia carcassa" and "Annulla rilascio" of the Bonifica card (Stage 6 slice 4). Release: the
// area, the optional kg, the read-only count of the parts registered from the car, and the two
// required ticks (the button stays disabled until both are set). Cancel: a required reason. The
// parent runs the write through its release session and passes back the saving state and an error
// text; emit "confirm" with the release body or { reason }.
export default {
  name: "CarBodyReleaseDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    // "release" | "cancel"
    mode: { type: String, default: "release" },
    // GET /compliance/dossiers/:id/car-body-release
    view: { type: Object, default: null },
    saving: { type: Boolean, default: false },
    // A translated error text, or ""
    error: { type: String, default: "" },
  },
  emits: ["update:modelValue", "confirm"],
  data: () => ({
    form: emptyReleaseForm(null),
    reason: "",
    reasonMax: REASON_MAX,
  }),
  computed: {
    areaItems() {
      return ((this.view && this.view.areas) || []).map((area) => ({
        value: area.id,
        title: area.name,
      }));
    },
    partsCount() {
      return (this.view && this.view.partsCount) || 0;
    },
    approvedDate() {
      return this.view && this.view.approvedAt
        ? formatRomeDate(this.view.approvedAt)
        : "";
    },
    weightError() {
      return releaseErrors(this.form).weightKg || null;
    },
    canSubmit() {
      return canSubmitRelease(this.form);
    },
    body() {
      return buildRelease(this.form);
    },
    reasonInvalid() {
      return reasonError(this.reason);
    },
  },
  watch: {
    // A new dialog starts empty: no ticks, no reason.
    modelValue(open) {
      if (!open) return;
      this.form = emptyReleaseForm(this.view);
      this.reason = "";
    },
  },
};
</script>

<style scoped>
.release-wrap {
  overflow-wrap: anywhere;
  white-space: normal;
}
</style>
